/**
 * Shared admin-style dashboard frame used by Operator and Cashier dashboards.
 *
 * Matches the main AdminDashboard look: sticky top bar with name + role
 * pill + logout, then a single horizontal tab nav (no "advanced" toggle —
 * operators/cashiers only have a slim set), then the tab content.
 *
 * Tabs are filtered by the active permissions returned from
 * /api/me/dashboard-permissions so the admin can switch any of them off.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const useDashboardPermissions = () => {
    const [tabs, setTabs] = useState(null); // null while loading, [] empty allowed
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/me/dashboard-permissions`, { withCredentials: true })
            .then(({ data }) => { if (alive) setTabs(data.tabs || []); })
            .catch(() => { if (alive) setTabs([]); });
        return () => { alive = false; };
    }, []);
    return tabs;
};

/**
 * RoleDashboardLayout
 *
 * Props:
 *  - title       : main heading (e.g. operator's storefront name)
 *  - subtitle    : small caption under the title
 *  - rolePill    : top-left pill text — "OPERATOR" / "CASHIER" etc.
 *  - rightLabel  : top-right label above rightValue (e.g. "Net house this week")
 *  - rightValue  : top-right value
 *  - tabs        : [{ value, label, icon: Icon, allowedKey }] — allowedKey gates visibility
 *  - allowed     : array of permitted tab keys (from useDashboardPermissions)
 *  - children    : map of tab content keyed by value: { value: <Component /> }
 *  - onLogout    : optional logout handler
 */
const RoleDashboardLayout = ({
    title, subtitle, rolePill, rightLabel, rightValue, tabs = [], allowed,
    children = {}, onLogout, fallback = null, headerExtras = null,
}) => {
    const [active, setActive] = useState(() => {
        const first = tabs.find(t => !t.allowedKey || (allowed || []).includes(t.allowedKey));
        return first?.value || (tabs[0]?.value ?? '');
    });

    useEffect(() => {
        if (!allowed) return;
        const stillAllowed = tabs.find(t => t.value === active && (!t.allowedKey || allowed.includes(t.allowedKey)));
        if (!stillAllowed) {
            const first = tabs.find(t => !t.allowedKey || allowed.includes(t.allowedKey));
            if (first) setActive(first.value);
        }
    }, [allowed, tabs, active]);

    const visibleTabs = tabs.filter(t => !t.allowedKey || (allowed || []).includes(t.allowedKey));

    if (allowed === null) {
        return (
            <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100">
            {/* Sticky top bar — mirrors AdminDashboard look */}
            <header className="sticky top-0 z-30 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            {rolePill && (
                                <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium bg-zinc-900 border border-zinc-800 px-2 py-0.5 rounded">
                                    {rolePill}
                                </span>
                            )}
                            <h1 className="text-sm font-semibold text-zinc-100 truncate" data-testid="role-dashboard-title">
                                {title}
                            </h1>
                        </div>
                        {subtitle && (
                            <p className="text-xs text-zinc-500 mt-0.5 truncate">{subtitle}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        {rightValue != null && (
                            <div className="text-right hidden sm:block">
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">{rightLabel}</p>
                                <p className="text-sm font-mono font-semibold text-zinc-100" data-testid="role-dashboard-right-value">
                                    {rightValue}
                                </p>
                            </div>
                        )}
                        {headerExtras}
                        {onLogout && (
                            <Button variant="outline" size="sm" onClick={onLogout}
                                className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                                data-testid="role-dashboard-logout">
                                <LogOut className="w-3.5 h-3.5" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Tab nav — same shape as AdminDashboard */}
                {visibleTabs.length > 0 && (
                    <div className="max-w-7xl mx-auto px-4 overflow-x-auto">
                        <Tabs value={active} onValueChange={setActive}>
                            <TabsList className="bg-transparent border-0 h-auto p-0 gap-1">
                                {visibleTabs.map((t) => {
                                    const Icon = t.icon;
                                    return (
                                        <TabsTrigger key={t.value} value={t.value}
                                            className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100 text-zinc-400 hover:text-zinc-200 px-4 h-9 rounded-md text-sm"
                                            data-testid={`role-tab-${t.value}`}>
                                            {Icon && <Icon className="w-3.5 h-3.5 mr-1.5" />}
                                            {t.label}
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>
                        </Tabs>
                    </div>
                )}
            </header>

            <main className="max-w-7xl mx-auto px-4 py-6">
                {visibleTabs.length === 0 ? (
                    <div className="rounded-md border border-zinc-800 bg-zinc-900/30 text-center py-16 text-zinc-500" data-testid="no-tabs">
                        {fallback || 'No dashboard sections are enabled for your role. Contact your admin.'}
                    </div>
                ) : (
                    visibleTabs.map(t => (
                        <div key={t.value} className={t.value === active ? '' : 'hidden'}>
                            {children[t.value] || null}
                        </div>
                    ))
                )}
            </main>
        </div>
    );
};

export default RoleDashboardLayout;
